/*
 * File: index.js
 * Project: seesaw-marketplace-app
 *
 * Created by Brendan Michaelsen on June 16, 2022 at 1:03 PM
 * Copyright © 2022 Seesaw Technologies, LLC. All rights reserved.
 *
 * Last Modified: September 25, 2023 at 12:20 PM
 * Modified By: Brendan Michaelsen
 */

// Polyfill IE
import 'react-app-polyfill/ie11';
import 'react-app-polyfill/stable';

// Modules
import React, { Suspense } from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { HelmetProvider } from 'react-helmet-async';
import { useSSR } from 'react-i18next';

// Analytics
import TagManager from 'react-gtm-module';

// Styles
import { config } from '@fortawesome/fontawesome-svg-core';
import { loadIcons } from '../styles/fontawesome';
import '@fortawesome/fontawesome-svg-core/styles.css';

// Utilities
import { buildInstance, buildOptions } from '../../utilities/i18n';

// Stores
import store from '../store';

// App
import App from './App';

// Components
import { Spinner } from '../components';

// Constants
import { DEFAULT_LOCALE } from '../../Constants';

// Configuration
config.autoAddCss = false;


/**
 * Constants
 */

const isDevelopment = process.env.ENV === 'development';
const isProduction = process.env.ENV === 'production';
const isHot = import.meta.webpackHot != null;


/**
 * Accept Hot Reload
 */

if (isHot && isDevelopment) {
	import.meta.webpackHot.accept();
}


/**
 * Initialize Google Tag Manager
 */

if (isProduction) {
	TagManager.initialize({ gtmId: process.env.GTM_ID });
}


/**
 * Load Icons
 */

loadIcons();


/**
 * Initialize i18n
 */

buildInstance({
	...buildOptions(false, isHot),
	loadPath: `${process.env.APP_URL}/locales/{{lng}}/{{ns}}.json`,
	addPath: `${process.env.APP_URL}/locales/{{lng}}/{{ns}}.missing.json`,
	backend: {
		loadPath: `${process.env.APP_URL}/locales/{{lng}}/{{ns}}.json`,
		addPath: `${process.env.APP_URL}/locales/{{lng}}/{{ns}}.missing.json`
	}
}, false);


/**
 * Render App
 */

// Determine render function
const isMarkupPresent = document.getElementById('root').hasChildNodes();
const renderFunction = isMarkupPresent ? ReactDOM.hydrate : ReactDOM.render;

// Create inner application component
const BaseApp = () => (
	<>
		<BrowserRouter>
			<App isHot={isHot} />
		</BrowserRouter>
		<Toaster />
	</>
);

// Create application component
const ParentApp = () => {

	// Get data if SSR mode
	if (!isHot) {

		// Get component state on client
		let state = { locale: { initialI18nStore: {}, initialLanguage: DEFAULT_LOCALE } };
		try { state = JSON.parse(decodeURIComponent(window.document.getElementById('data-state').innerHTML)); } catch (e) { }

		// Use SSR from hook
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useSSR(state.locale.initialI18nStore, state.locale.initialLanguage);
	}

	// Render components
	return (
		<React.StrictMode>
			<Provider store={store}>
				<HelmetProvider>
					{isHot ? (
						<Suspense fallback={<Spinner showMeta />}>
							<BaseApp />
						</Suspense>
					) : (
						<BaseApp />
					)}
				</HelmetProvider>
			</Provider>
		</React.StrictMode>
	);
};

// Prepare content
renderFunction(
	<ParentApp />,
	document.getElementById('root')
);
